import api from '../../../api/client';
import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Tag,
  Modal,
  message,
  Tooltip,
  Typography,
  Row,
  Col,
  Statistic,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { usePermissions } from "../../../permissions";
import config from "../../../config";
import SalaryCalculator from "./SalaryCalculator";

const { Title, Text } = Typography;

const PayrollList = () => {
  const [data, setData] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const { createPermission, editPermission, deletePermission } = usePermissions();
  const environment = window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;

  // Fetch payroll data
  const fetchPayroll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/employees_salary`);
      setData(data);
    } catch (err) {
      message.error("Failed to fetch payroll data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch users for employee names
  const fetchUsers = async () => {
    try {
      const { data } = await api.get(`/auth`);
      setUsers(data);
    } catch (err) {
      console.error("Failed to fetch users", err);
    }
  };

  useEffect(() => {
    fetchPayroll();
    fetchUsers();
  }, []);

  const handleRefresh = () => {
    fetchPayroll();
  };

  const handleEdit = (record) => {
    // Navigate to edit page or open edit modal
    // history.push(`edit-salary/${record.id}`);
    message.info(`Edit payroll for ${getEmployeeName(record.employee)}`);
  };

  const handleDelete = async (record) => {
    Modal.confirm({
      title: "Delete Payroll",
      content: `Are you sure you want to delete payroll for ${getEmployeeName(record.employee)}?`,
      onOk: async () => {
        try {
          await api.delete(`/employees_salary/${record.id}`);
          message.success("Payroll deleted successfully");
          fetchPayroll();
        } catch (err) {
          message.error("Failed to delete payroll");
        }
      },
    });
  };

  const getEmployeeName = (employeeId) => {
    const user = users.find((u) => u.id === employeeId);
    return user ? `${user.firstname} ${user.lastname}` : "-";
  };

  // Export CSV
  const exportToCSV = () => {
    if (!filteredData.length) return message.warning("No data to export");
    const headers = ["Period", "Employee", "Gross Salary", "Total Deductions", "Net Salary", "Created"];
    const rows = filteredData.map((row) => [
      row.period,
      getEmployeeName(row.employee),
      row.gross?.toLocaleString() || "0",
      row.totalDeductions?.toLocaleString() || "0",
      row.net?.toLocaleString() || "0",
      dayjs(row.createdAt).format("YYYY-MM-DD HH:mm"),
    ]);
    const csvContent = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `payroll_${dayjs().format("YYYYMMDD_HHmmss")}.csv`);
    message.success("CSV exported");
  };

  // Export PDF
  const exportToPDF = () => {
    if (!filteredData.length) return message.warning("No data to export");
    const doc = new jsPDF();
    doc.autoTable({
      head: [["Period", "Employee", "Gross Salary", "Total Deductions", "Net Salary", "Created"]],
      body: filteredData.map((row) => [
        row.period,
        getEmployeeName(row.employee),
        row.gross?.toLocaleString() || "0",
        row.totalDeductions?.toLocaleString() || "0",
        row.net?.toLocaleString() || "0",
        dayjs(row.createdAt).format("YYYY-MM-DD HH:mm"),
      ]),
    });
    doc.save(`payroll_${dayjs().format("YYYYMMDD_HHmmss")}.pdf`);
    message.success("PDF exported");
  };

  // Filter data
  const filteredData = searchText
    ? data.filter((item) => {
        const employeeName = getEmployeeName(item.employee);
        return (
          item.period?.toLowerCase().includes(searchText.toLowerCase()) ||
          employeeName?.toLowerCase().includes(searchText.toLowerCase())
        );
      })
    : data;

  // Calculate statistics
  const totalPayroll = filteredData.reduce((sum, row) => sum + (row.net || 0), 0);
  const totalDeductions = filteredData.reduce((sum, row) => sum + (row.totalDeductions || 0), 0);
  const totalGross = filteredData.reduce((sum, row) => sum + (row.gross || 0), 0);

  // Table columns
  const columns = [
    { title: "Period", dataIndex: "period", key: "period", sorter: (a, b) => a.period.localeCompare(b.period) },
    {
      title: "Employee",
      key: "employee",
      render: (_, record) => getEmployeeName(record.employee),
      sorter: (a, b) => getEmployeeName(a.employee).localeCompare(getEmployeeName(b.employee)),
    },
    {
      title: "Gross Salary",
      dataIndex: "gross",
      key: "gross",
      render: (val) => `KES ${val?.toLocaleString() || "0.00"}`,
      align: "right",
      sorter: (a, b) => (a.gross || 0) - (b.gross || 0),
    },
    {
      title: "Total Deductions",
      dataIndex: "totalDeductions",
      key: "deductions",
      render: (val) => `KES ${val?.toLocaleString() || "0.00"}`,
      align: "right",
      sorter: (a, b) => (a.totalDeductions || 0) - (b.totalDeductions || 0),
    },
    {
      title: "Net Salary",
      dataIndex: "net",
      key: "net",
      render: (val) => <Tag color="green">KES {val?.toLocaleString() || "0.00"}</Tag>,
      align: "right",
      sorter: (a, b) => (a.net || 0) - (b.net || 0),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "created",
      render: (date) => dayjs(date).format("DD/MM/YYYY HH:mm"),
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          {editPermission && (
            <Tooltip title="Edit">
              <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
            </Tooltip>
          )}
          {deletePermission && (
            <Tooltip title="Delete">
              <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDelete(record)} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic title="Total Gross Salary" value={totalGross} prefix="KES " precision={2} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic title="Total Deductions" value={totalDeductions} prefix="KES " precision={2} valueStyle={{ color: "#cf1322" }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic title="Total Net Payroll" value={totalPayroll} prefix="KES " precision={2} valueStyle={{ color: "#3f8600" }} />
          </Card>
        </Col>
      </Row>

      {/* Main Card */}
      <Card
        title="Payroll List"
        extra={
          <Space>
            <Input.Search
              placeholder="Search by period or employee..."
              allowClear
              style={{ width: 250 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Button icon={<FileExcelOutlined />} onClick={exportToCSV}>
              CSV
            </Button>
            <Button icon={<FilePdfOutlined />} onClick={exportToPDF}>
              PDF
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
              Refresh
            </Button>
            {createPermission && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>
                Generate Payroll
              </Button>
            )}
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          scroll={{ x: "max-content" }}
          size="small"
        />
      </Card>

      {/* Generate Payroll Modal */}
      <SalaryCalculator
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSuccess={() => {
          setModalVisible(false);
          fetchPayroll();
        }}
      />
    </>
  );
};

export default PayrollList;